import {
  DataType,
  Form,
  FormOptions,
  FormSkeleton,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Box, Breadcrumbs, Button, Typography } from '@material-ui/core'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumTipoProductoCapacidad } from '../../../api'
import Page from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import { enumToSelectOptionArray } from '../../../helpers'
import { getVersionNumberFromArray } from '../../../helpers/getVersionNumberFromArray'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { useConfirmationDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { getCustomerDomain } from '../../../redux'
import { ProcedureFormMode } from '../../procedures/procedures.types'
import {
  CapacityFormModel,
  ContractCapacitiesFormModel,
  EnumContractCapacitiesOptions
} from '../contracts.model'
import { fetchCapacitiesInBetweenRange, updateContractCapacities } from '../contracts.service'

interface ContractCapacitiesPageProps {
  location?: {
    state: {
      rowCapacity?: CapacityFormModel
      clientID: number
      clientLinkText: string
      contractId?: number
      contractCode?: string
      tollSelectOptionList: SelectOption[]
    }
  }
}

export const ContractCapacitiesPage: React.FC<ContractCapacitiesPageProps> = props => {
  const capacityRowData = props.location?.state?.rowCapacity
  const clientID = props.location?.state?.clientID
  const clientText = props.location?.state?.clientLinkText
  const contractId = props.location?.state?.contractId
  const contractCode = props.location?.state?.contractCode
  const tollSelectOptionList = props.location?.state?.tollSelectOptionList
  const today = moment().startOf('day').toDate()

  const isTodayInRange =
    moment(today).isAfter(capacityRowData?.startDate!) &&
    moment(today).isBefore(capacityRowData?.endDate!)

  const [capacityFormData, setCapacityFormData] = useState<ContractCapacitiesFormModel>({
    ...capacityRowData,
    modRequest: EnumContractCapacitiesOptions.Toll,
    newCode:
      capacityRowData &&
      getVersionNumberFromArray([capacityRowData], 'code', capacityRowData.code, 'brackets'),
    newStartDate: isTodayInRange
      ? moment().format('YYYY-MM-DD')
      : moment(capacityRowData?.startDate!).add(1, 'day').format('YYYY-MM-DD'),
    newEndDate: capacityRowData?.endDate,
    endDate: isTodayInRange
      ? moment().subtract(1, 'day').startOf('day').toDate()
      : capacityRowData?.startDate!,
    hasCapBetweenDates: false
  })
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [pendingChanges, setPendingChanges] = useState<boolean>(false)
  const [showNewToll, setShowNewToll] = useState<boolean>(true)
  const [showNewCapacity, setShowNewCapacity] = useState<boolean>(false)
  const [showBoth, setShowBoth] = useState<boolean>(false)

  const [t] = useTranslation(['globalerisLibrary', 'enums', 'contracts'])
  const history = useHistory()
  const { info, error } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const minNewStartDate = moment.max(
    moment(capacityFormData?.startDate!).add(1, 'day'),
    moment().startOf('day')
  )
  const parseDateValue = (value: any) => {
    if (!value) return moment.invalid()
    if (value instanceof Date) return moment(value)

    const parsedDate = moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY', 'D/M/YYYY'], true)
    return parsedDate.isValid() ? parsedDate : moment(value)
  }

  const newEndDateIsBeforeToday =
    capacityFormData.newEndDate &&
    parseDateValue(capacityFormData.newEndDate).isValid() &&
    moment().startOf('day').isAfter(parseDateValue(capacityFormData.newEndDate), 'day')
  const newStartDateIsBeforeToday =
    capacityFormData.newStartDate &&
    parseDateValue(capacityFormData.newStartDate).isValid() &&
    moment().startOf('day').isAfter(parseDateValue(capacityFormData.newStartDate), 'day')
  const newStartDateIsOutOfRange =
    capacityFormData.newStartDate &&
    parseDateValue(capacityFormData.newStartDate).isValid() &&
    (parseDateValue(capacityFormData.newStartDate).isBefore(
      moment(capacityFormData?.startDate!).add(1, 'day'),
      'day'
    ) ||
      parseDateValue(capacityFormData.newStartDate).isAfter(
        parseDateValue(capacityFormData.newEndDate),
        'day'
      ))

  // #region Handlers Starts
  const handleFormDataChange = (formState: any, fieldName: string, value: any) => {
    setCapacityFormData({
      ...formState,
      [fieldName]: value
    } as ContractCapacitiesFormModel)
  }

  const handleNewEndDateChange = (value: any, formState: any) => {
    setCapacityFormData({
      ...formState,
      newEndDate:
        value && parseDateValue(value).isValid()
          ? parseDateValue(value).format('YYYY-MM-DD')
          : value
    } as ContractCapacitiesFormModel)
  }

  const handleModRequestChange = (value: string | number) => {
    switch (value) {
      case EnumContractCapacitiesOptions.Toll:
        setShowNewToll(true)
        setShowNewCapacity(false)
        setShowBoth(false)
        break

      case EnumContractCapacitiesOptions.Capacity:
        setShowNewCapacity(true)
        setShowNewToll(false)
        setShowBoth(false)
        break

      case EnumContractCapacitiesOptions.Both:
        setShowBoth(true)
        setShowNewToll(false)
        setShowNewCapacity(false)
        break

      default:
        break
    }
  }

  const onCapacityUpdate = async () => {
    const newEndDateValue = capacityFormData.newEndDate
    const newEndDateMoment = parseDateValue(newEndDateValue)

    if (
      newEndDateValue &&
      newEndDateMoment.isValid() &&
      moment().startOf('day').isAfter(newEndDateMoment, 'day')
    ) {
      error(t('contracts:notifications.errors.newEndDatePast'))
      return
    }

    if (newStartDateIsBeforeToday) {
      error(t('contracts:notifications.errors.newStartDatePast'))
      return
    }

    if (newStartDateIsOutOfRange) {
      error(t('contracts:notifications.errors.newStartDateOutOfRange'))
      return
    }

    if (
      !missingFieldsFormWithRequiredText(contractCapacitiesFormDefinition, capacityFormData) &&
      contractId
    ) {
      await fetchCapacitiesInBetweenRange(
        contractId,
        moment(capacityFormData.newStartDate).format('YYYY-MM-DD'),
        moment(capacityFormData.newEndDate).format('YYYY-MM-DD')
      ).then(response => {
        if (response.success) {
          let patchRequest: ContractCapacitiesFormModel = {
            ...capacityFormData,
            newStartDate: moment(capacityFormData.newStartDate).format('YYYY-MM-DD'),
            newEndDate: moment(capacityFormData.newEndDate).format('YYYY-MM-DD'),
            endDate: moment(capacityFormData.newStartDate).subtract(1, 'days').format('YYYY-MM-DD'),
            hasCapBetweenDates: false
          }
          if (response.data && response.data > 0) {
            getConfirmation(ConfirmationDialogs.CONTRACT_CAPACITIES_MASSIVE_CHANGE, undefined, true)
              .then(() => {
                patchRequest = {
                  ...patchRequest,
                  hasCapBetweenDates: true
                }
              })
              .catch(() => {
                patchRequest = {
                  ...patchRequest,
                  hasCapBetweenDates: false
                }
              })
              .finally(() => {
                closeConfirmation()
                patchContractCapacities(patchRequest)
              })
          } else if (response.data === 0) {
            // there is not any capacities in that date period. Continue tu update ContractCapacity
            patchContractCapacities(patchRequest)
          }
        } else {
          error(t('contracts:forms.contractCapacities.notifications.error.fetchingCapacitiesError'))
        }
      })
    } else {
      error(t('contracts:forms.contractCapacities.notifications.error.missingFieldsForm'))
    }
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const patchContractCapacities = async (patchRequest: ContractCapacitiesFormModel) => {
    await updateContractCapacities(patchRequest, patchRequest.id!).then(response => {
      if (response.success) {
        info(t('contracts:forms.contractCapacities.notifications.save.success'))
        history.push(NonAuthRoutes.ProceduresAdd, {
          contractId: contractId,
          capacityNewStartDate: capacityFormData.newStartDate,
          capacityCode: capacityFormData.newCode,
          formMode: ProcedureFormMode.MODIFICACION_TECNICA,
          admChangeReasons: capacityFormData.modRequest
        })
      } else {
        error(t('contracts:forms.contractCapacities.notifications.save.error'))
      }
    })
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const contractCapacitiesFormOptions: FormOptions = {
    elevation: 1,
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('contracts:forms.contractCapacities.analytics')
    }
  }

  const contractCapacitiesFormDefinition: Section[] = [
    {
      title: t('contracts:forms.contractCapacities.titles.capacityData'),
      inputs: [
        {
          name: 'newCode',
          label: t('contracts:forms.contractCapacities.inputs.newCode'),
          type: DataType.TEXT,
          inputSize: 6,
          required: true,
          onChange: (value, formState) => {
            handleFormDataChange(formState, 'newCode', value)
          }
        },
        {
          name: 'type',
          label: t('contracts:components.capacitiesTable.actions.edit.inputs.contractCapacityType'),
          type: DataType.SELECT,
          inputSize: 6,
          selectOptions: enumToSelectOptionArray(
            EnumTipoProductoCapacidad,
            'enums:EnumTipoProductoCapacidad'
          ),
          readOnly: true
        },
        {
          name: 'toll',
          label: t('contracts:components.capacitiesTable.actions.edit.inputs.toll'),
          type: DataType.SELECT,
          selectOptions: tollSelectOptionList,
          readOnly: true
        },
        {
          name: 'qaEstimada',
          label: t('contracts:components.capacitiesTable.actions.edit.inputs.qa'),
          suffix: t('contracts:forms.add.units.kWh'),
          type: DataType.NUMBER,
          customDecimalScale: 0,
          readOnly: true
        },
        {
          name: 'qdContratada',
          label: t('contracts:components.capacitiesTable.actions.edit.inputs.qd'),
          suffix: t('contracts:forms.add.units.kWh'),
          type: DataType.NUMBER,
          readOnly: true
        },
        {
          name: 'startDate',
          label: t('contracts:components.capacitiesTable.actions.edit.inputs.startDate'),
          type: DataType.DATE,
          inputSize: 6,
          required: true,
          readOnly: true
        },
        {
          name: 'newEndDate',
          label: t('contracts:components.capacitiesTable.actions.edit.inputs.endDate'),
          type: DataType.DATE,
          inputSize: 6,
          required: true,
          readOnly: true
        }
      ]
    },
    {
      title: t('contracts:forms.contractCapacities.titles.modRequest'),
      inputs: [
        {
          name: 'modRequest',
          label: t('contracts:forms.contractCapacities.inputs.modRequest'),
          type: DataType.SELECT,
          required: true,
          selectOptions: enumToSelectOptionArray(
            EnumContractCapacitiesOptions,
            'enums:EnumContractCapacitiesOptions'
          ),
          onChange: (value, formState) => {
            handleFormDataChange(formState, 'modRequest', value)
            handleModRequestChange(value)
          }
        },
        {
          name: 'newToll',
          label: t('contracts:components.capacitiesTable.actions.edit.inputs.toll'),
          type: DataType.SELECT,
          display: showNewToll || showBoth,
          required: showNewToll || showBoth,
          selectOptions: tollSelectOptionList,
          onChange: (value, formState) => {
            handleFormDataChange(formState, 'newToll', value)
          }
        },
        {
          name: 'newQdContratada',
          label: t('contracts:components.capacitiesTable.actions.edit.inputs.qd'),
          suffix: t('contracts:forms.add.units.kWh'),
          display: showNewCapacity || showBoth,
          required: showNewCapacity || showBoth,
          type: DataType.NUMBER,
          onChange: (value, formState) => {
            handleFormDataChange(formState, 'newQdContratada', value)
          }
        },
        {
          name: 'newStartDate',
          label: t('contracts:forms.contractCapacities.inputs.newStartDate'),
          type: DataType.DATE,
          inputSize: 6,
          helpText: t('contracts:forms.contractCapacities.helpText.newStartDate'),
          disablePast: true,
          minDate: minNewStartDate.format('YYYY-MM-DD'),
          maxDate: moment(capacityFormData.newEndDate).format('YYYY-MM-DD'),
          errorState: !!newStartDateIsBeforeToday || !!newStartDateIsOutOfRange,
          errorText: newStartDateIsBeforeToday
            ? t('contracts:notifications.errors.newStartDatePast')
            : newStartDateIsOutOfRange
              ? t('contracts:notifications.errors.newStartDateOutOfRange')
              : '',
          onChange: (value, formState) => {
            if (!value || !moment(value).isValid()) return

            const newStartDate = moment(value)

            setCapacityFormData({
              ...formState,
              newStartDate: newStartDate.format('YYYY-MM-DD'),
              endDate: newStartDate.clone().subtract(1, 'day').format('YYYY-MM-DD')
            } as any)
          }
        },
        {
          name: 'newEndDate',
          label: t('contracts:components.capacitiesTable.actions.edit.inputs.endDate'),
          type: DataType.DATE,
          inputSize: 6,
          required: true,
          errorState: !!newEndDateIsBeforeToday,
          errorText: newEndDateIsBeforeToday
            ? t('contracts:notifications.errors.newEndDatePast')
            : '',
          onChange: (value, formState) => {
            handleNewEndDateChange(value, formState)
          },
          onBlur: value => {
            handleNewEndDateChange(value, capacityFormData)
          },
          readOnly: true
        }
      ]
    }
  ]

  const requiredFieldsAreMissing = contractCapacitiesFormDefinition.some(section =>
    section.inputs.some(input => {
      if (input.display === false || !input.required) return false

      const nameParts = input.name.split('.')
      const value = nameParts.reduce((data, key) => data?.[key], capacityFormData as any)

      return value === undefined || value === null || value === ''
    })
  )
  const disableSave =
    !!newEndDateIsBeforeToday ||
    !!newStartDateIsBeforeToday ||
    !!newStartDateIsOutOfRange ||
    requiredFieldsAreMissing

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('contracts:forms.contractCapacities.titles.main')}>
        <Button
          variant="text"
          color="primary"
          size="small"
          onClick={() => {
            history.goBack()
          }}
        >
          {t('contracts:forms.contractCapacities.mainActions.back')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={disableSave}
          onClick={onCapacityUpdate}
        >
          {t('contracts:forms.contractCapacities.mainActions.save')}
        </Button>
      </PageHeader>
      <PageCenterColumn>
        <Box mb={1}>
          <Breadcrumbs separator=">" aria-label="breadcrumb">
            {clientID && (
              <BreadcrumbLink
                toPathName="/client/file"
                state={{ clientID: clientID }}
                confirmation={pendingChanges}
              >
                {clientText}
              </BreadcrumbLink>
            )}
            <Typography key="3" color="primary" variant="subtitle2">
              {t('contracts:breadcrumbs.contract', { contractCode: contractCode })}
            </Typography>
            {/* <Typography key="4" color="primary" variant="subtitle1">
              {t('contracts:breadcrumbs.productCapacity', { capacityCode: capacityRowData?.code })}
            </Typography> */}
          </Breadcrumbs>
        </Box>
        {isFetching ? (
          <FormSkeleton inputs={3} columns={3} elevation={1} withTitle />
        ) : (
          <Form
            title={t('contracts:forms.contractCapacities.titles.secondary', {
              capacityCode: capacityFormData?.newCode
            })}
            sections={contractCapacitiesFormDefinition}
            data={capacityFormData}
            options={contractCapacitiesFormOptions}
          />
        )}
      </PageCenterColumn>
    </Page>
  )
}
